// Loan eligibility math — all amounts in INR.
// Each method returns { eligibility, monthlyAffordableEMI, computedMonthlyIncome, notes, breakdown }

const r = (x) => Math.max(0, Math.round(x || 0));

// PV of an annuity given a monthly EMI affordable
// loan = EMI × [(1 - (1+r)^-n) / r]
export function loanFromEMI(monthlyEMI, annualRatePct, tenureMonths) {
  const rate = (annualRatePct || 0) / 12 / 100;
  const n = Math.max(1, Math.round(tenureMonths || 0));
  if (rate === 0) return Math.max(0, monthlyEMI * n);
  const pv = monthlyEMI * (1 - Math.pow(1 + rate, -n)) / rate;
  return Math.max(0, pv);
}

// EMI for a loan
export function emiOfLoan(loan, annualRatePct, tenureMonths) {
  const rate = (annualRatePct || 0) / 12 / 100;
  const n = Math.max(1, Math.round(tenureMonths || 0));
  if (rate === 0) return loan / n;
  const pow = Math.pow(1 + rate, n);
  return (loan * rate * pow) / (pow - 1);
}

// 1. BANKING METHOD — unsecured business loan from avg monthly credits
export function bankingMethod(i) {
  const avgCredits = +i.avgMonthlyCredits || 0;
  const margin = (+i.assumedMarginPct || 0) / 100;
  const foir = (+i.foirPct || 65) / 100;
  const existingEMI = +i.existingEMI || 0;
  const tenure = +i.tenureMonths || 36;
  const rate = +i.interestRatePct || 16;

  const impliedMonthlyProfit = avgCredits * margin;
  const monthlyAffordableEMI = Math.max(0, impliedMonthlyProfit * foir - existingEMI);
  const eligibility = loanFromEMI(monthlyAffordableEMI, rate, tenure);
  return {
    eligibility: r(eligibility),
    monthlyAffordableEMI: r(monthlyAffordableEMI),
    computedMonthlyIncome: r(impliedMonthlyProfit),
    notes:
      "Banks/NBFCs read 6–12 months of current-account credits, ignore self-transfers and apply an industry-typical profit margin. Strongest where genuine sales credits land in the bank.",
    breakdown: [
      { label: "Avg monthly credits", v: r(avgCredits) },
      { label: `Assumed profit margin (${(margin * 100).toFixed(0)}%)`, v: r(impliedMonthlyProfit) },
      { label: `FOIR cap (${(foir * 100).toFixed(0)}%)`, v: r(impliedMonthlyProfit * foir) },
      { label: "Less: existing EMIs", v: r(existingEMI), neg: true },
      { label: "Affordable EMI", v: r(monthlyAffordableEMI), bold: true },
    ],
  };
}

// 2. GST METHOD — annual turnover from GSTR
export function gstMethod(i) {
  const annualTurnover = +i.annualTurnover || 0;
  const margin = (+i.assumedMarginPct || 0) / 100;
  const foir = (+i.foirPct || 65) / 100;
  const existingEMI = +i.existingEMI || 0;
  const tenure = +i.tenureMonths || 36;
  const rate = +i.interestRatePct || 16;

  const monthlyTurnover = annualTurnover / 12;
  const monthlyProfit = monthlyTurnover * margin;
  const monthlyAffordableEMI = Math.max(0, monthlyProfit * foir - existingEMI);
  const eligibility = loanFromEMI(monthlyAffordableEMI, rate, tenure);
  // Common rule-of-thumb cap for GST programmes
  const ruleOfThumbCap = annualTurnover * (+i.thumbMultiplier || 0.5);
  return {
    eligibility: r(Math.min(eligibility, ruleOfThumbCap || eligibility)),
    monthlyAffordableEMI: r(monthlyAffordableEMI),
    computedMonthlyIncome: r(monthlyProfit),
    notes:
      "GST programmes assess last 12 months of GSTR turnover. Most lenders cap at ~50% of annual turnover; some go up to 1× for top-graded cases.",
    breakdown: [
      { label: "Annual GST turnover", v: r(annualTurnover) },
      { label: "Monthly turnover", v: r(monthlyTurnover) },
      { label: `Assumed profit (${(margin * 100).toFixed(0)}%)`, v: r(monthlyProfit) },
      { label: `FOIR cap (${(foir * 100).toFixed(0)}%)`, v: r(monthlyProfit * foir) },
      { label: "Less: existing EMIs", v: r(existingEMI), neg: true },
      { label: "Affordable EMI", v: r(monthlyAffordableEMI), bold: true },
      { label: `Rule-of-thumb cap (${((+i.thumbMultiplier || 0.5)).toFixed(1)}× turnover)`, v: r(ruleOfThumbCap) },
    ],
  };
}

// 3. LOW LTV — property/asset backed
export function lowLTVMethod(i) {
  const propertyValue = +i.propertyValue || 0;
  const ltv = (+i.ltvPct || 60) / 100;
  const tenure = +i.tenureMonths || 180;
  const rate = +i.interestRatePct || 10.5;
  const existingEMI = +i.existingEMI || 0;
  const monthlyIncome = +i.monthlyIncome || 0;
  const foir = (+i.foirPct || 60) / 100;

  const ltvCap = propertyValue * ltv;
  const monthlyAffordableEMI = Math.max(0, monthlyIncome * foir - existingEMI);
  const incomeBackedLoan = loanFromEMI(monthlyAffordableEMI, rate, tenure);
  const eligibility = monthlyIncome > 0 ? Math.min(ltvCap, incomeBackedLoan) : ltvCap;
  return {
    eligibility: r(eligibility),
    monthlyAffordableEMI: r(monthlyAffordableEMI),
    computedMonthlyIncome: r(monthlyIncome),
    notes:
      "LAP and home loan use Loan-To-Value as a hard ceiling (LAP 50–70%, home loan up to 90%). Income-FOIR check then governs how much of that LTV cap is actually disbursable.",
    breakdown: [
      { label: "Property value", v: r(propertyValue) },
      { label: `LTV cap (${(ltv * 100).toFixed(0)}%)`, v: r(ltvCap), bold: true },
      ...(monthlyIncome > 0
        ? [
          { label: "Monthly income", v: r(monthlyIncome) },
          { label: `FOIR cap (${(foir * 100).toFixed(0)}%)`, v: r(monthlyIncome * foir) },
          { label: "Less: existing EMIs", v: r(existingEMI), neg: true },
          { label: "Affordable EMI", v: r(monthlyAffordableEMI) },
          { label: "Income-backed loan capacity", v: r(incomeBackedLoan) },
        ]
        : []),
    ],
  };
}

// 4. RTR FOR BT — repayment track record-based balance transfer + top-up
export function rtrBTMethod(i) {
  const outstanding = +i.outstandingPrincipal || 0;
  const monthsClean = +i.cleanRTRMonths || 0;
  const topUpPct = (+i.topUpPct || 25) / 100;
  const currentRate = +i.currentRatePct || 14;
  const newRate = +i.newRatePct || 11;
  const remainingTenure = +i.remainingTenureMonths || 60;

  const eligibleForTopUp = monthsClean >= 12;
  const topUp = eligibleForTopUp ? outstanding * topUpPct : 0;
  const eligibility = outstanding + topUp;
  const oldEMI = emiOfLoan(outstanding, currentRate, remainingTenure);
  const newEMI = emiOfLoan(eligibility, newRate, remainingTenure);
  const monthlySavings = oldEMI - emiOfLoan(outstanding, newRate, remainingTenure);
  const totalSavings = monthlySavings * remainingTenure;
  return {
    eligibility: r(eligibility),
    monthlyAffordableEMI: r(newEMI),
    computedMonthlyIncome: 0,
    notes:
      "Balance Transfer logic: clean repayment ≥12 months unlocks top-up (typically 20–30%). Net benefit is rate arbitrage savings + bonus headroom.",
    breakdown: [
      { label: "Outstanding principal", v: r(outstanding) },
      { label: `Top-up @ ${(topUpPct * 100).toFixed(0)}% ${eligibleForTopUp ? "" : "(needs ≥12 months clean)"}`, v: r(topUp) },
      { label: "BT + Top-up amount", v: r(eligibility), bold: true },
      { label: `Current EMI @ ${currentRate}%`, v: r(oldEMI) },
      { label: `New EMI @ ${newRate}%`, v: r(newEMI) },
      { label: `Monthly savings (BT only)`, v: r(monthlySavings) },
      { label: `Total savings over remaining tenure`, v: r(totalSavings) },
    ],
  };
}

// 5. GROSS PROFIT METHOD — from audited financials
export function grossProfitMethod(i) {
  const revenue = +i.annualRevenue || 0;
  const cogs = +i.annualCOGS || 0;
  const foir = (+i.foirPct || 65) / 100;
  const existingEMI = +i.existingEMI || 0;
  const tenure = +i.tenureMonths || 36;
  const rate = +i.interestRatePct || 14;

  const grossProfit = Math.max(0, revenue - cogs);
  const monthlyGP = grossProfit / 12;
  const monthlyAffordableEMI = Math.max(0, monthlyGP * foir - existingEMI);
  const eligibility = loanFromEMI(monthlyAffordableEMI, rate, tenure);
  return {
    eligibility: r(eligibility),
    monthlyAffordableEMI: r(monthlyAffordableEMI),
    computedMonthlyIncome: r(monthlyGP),
    notes:
      "Used for cases with audited P&L. Stronger evidence than GST/banking — banks accept higher tickets, but tenure and rate negotiate harder around DSCR.",
    breakdown: [
      { label: "Annual revenue", v: r(revenue) },
      { label: "Less: COGS", v: r(cogs), neg: true },
      { label: "Annual Gross Profit", v: r(grossProfit), bold: true },
      { label: "Monthly GP", v: r(monthlyGP) },
      { label: `FOIR cap (${(foir * 100).toFixed(0)}%)`, v: r(monthlyGP * foir) },
      { label: "Less: existing EMIs", v: r(existingEMI), neg: true },
      { label: "Affordable EMI", v: r(monthlyAffordableEMI) },
    ],
  };
}

// 6. GROSS RECEIPTS (Professionals — 44ADA presumptive)
export function grossReceiptsMethod(i) {
  const receipts = +i.annualGrossReceipts || 0;
  const presumptivePct = (+i.presumptivePct || 50) / 100;
  const foir = (+i.foirPct || 65) / 100;
  const existingEMI = +i.existingEMI || 0;
  const tenure = +i.tenureMonths || 60;
  const rate = +i.interestRatePct || 13;

  const annualNet = receipts * presumptivePct;
  const monthlyNet = annualNet / 12;
  const monthlyAffordableEMI = Math.max(0, monthlyNet * foir - existingEMI);
  const eligibility = loanFromEMI(monthlyAffordableEMI, rate, tenure);
  return {
    eligibility: r(eligibility),
    monthlyAffordableEMI: r(monthlyAffordableEMI),
    computedMonthlyIncome: r(monthlyNet),
    notes:
      "Doctors, CAs, lawyers, architects and consultants — 44ADA presumes 50% of gross receipts as professional income. Many NBFCs accept this directly without P&L.",
    breakdown: [
      { label: "Annual gross receipts", v: r(receipts) },
      { label: `Presumptive income (${(presumptivePct * 100).toFixed(0)}%)`, v: r(annualNet), bold: true },
      { label: "Monthly net professional income", v: r(monthlyNet) },
      { label: `FOIR cap (${(foir * 100).toFixed(0)}%)`, v: r(monthlyNet * foir) },
      { label: "Less: existing EMIs", v: r(existingEMI), neg: true },
      { label: "Affordable EMI", v: r(monthlyAffordableEMI) },
    ],
  };
}

// 7. INCOME MULTIPLIER (Salaried)
export function incomeMultiplierMethod(i) {
  const takeHome = +i.netMonthlyTakeHome || 0;
  const foir = (+i.foirPct || 55) / 100;
  const existingEMI = +i.existingEMI || 0;
  const tenure = +i.tenureMonths || 240;
  const rate = +i.interestRatePct || 8.75;
  const multiplierMonths = +i.multiplierMonths || 72;

  const monthlyAffordableEMI = Math.max(0, takeHome * foir - existingEMI);
  const emiBased = loanFromEMI(monthlyAffordableEMI, rate, tenure);
  const multiplierBased = takeHome * multiplierMonths;
  const eligibility = Math.min(emiBased, multiplierBased);
  return {
    eligibility: r(eligibility),
    monthlyAffordableEMI: r(monthlyAffordableEMI),
    computedMonthlyIncome: r(takeHome),
    notes:
      "Most common for home loans. Lower of (FOIR-based EMI capacity) and (take-home × 60–72 months). Pre-approved offers from top corporates can push tenure to 30 yrs.",
    breakdown: [
      { label: "Net monthly take-home", v: r(takeHome) },
      { label: `FOIR cap (${(foir * 100).toFixed(0)}%)`, v: r(takeHome * foir) },
      { label: "Less: existing EMIs", v: r(existingEMI), neg: true },
      { label: "Affordable EMI", v: r(monthlyAffordableEMI) },
      { label: "EMI-based loan capacity", v: r(emiBased) },
      { label: `Multiplier rule (${multiplierMonths}× take-home)`, v: r(multiplierBased) },
    ],
  };
}

// 8. PROJECTED EBITDA — for project finance / large CapEx
export function projectedEBITDAMethod(i) {
  const ebitda = +i.projectedAnnualEBITDA || 0;
  const dscr = +i.targetDSCR || 1.25;
  const tenure = +i.tenureMonths || 84;
  const rate = +i.interestRatePct || 11.5;

  const debtServiceCapacity = ebitda / Math.max(1, dscr);
  const monthlyDebtService = debtServiceCapacity / 12;
  const eligibility = loanFromEMI(monthlyDebtService, rate, tenure);
  return {
    eligibility: r(eligibility),
    monthlyAffordableEMI: r(monthlyDebtService),
    computedMonthlyIncome: r(ebitda / 12),
    notes:
      "Project finance uses CMA-projected EBITDA at stabilisation, divided by target DSCR (typically 1.20–1.40). Lender will also assess promoter equity, sponsor strength and project risk.",
    breakdown: [
      { label: "Projected annual EBITDA at stabilisation", v: r(ebitda) },
      { label: `Target DSCR (${dscr})`, v: dscr, raw: true },
      { label: "Annual debt service capacity", v: r(debtServiceCapacity) },
      { label: "Monthly debt service", v: r(monthlyDebtService), bold: true },
    ],
  };
}

export const METHOD_DEFS = [
  { key: "banking", title: "Banking Method", short: "Banking", suitable: "Avg monthly credits ≥ ₹3 L · current-account-led businesses", calc: bankingMethod, defaults: { avgMonthlyCredits: 800000, assumedMarginPct: 12, foirPct: 65, existingEMI: 0, tenureMonths: 36, interestRatePct: 16 } },
  { key: "gst", title: "GST Method", short: "GST", suitable: "Annual turnover ≥ ₹40 L · regular GST filers", calc: gstMethod, defaults: { annualTurnover: 30000000, assumedMarginPct: 10, foirPct: 65, existingEMI: 0, tenureMonths: 36, interestRatePct: 15.5, thumbMultiplier: 0.5 } },
  { key: "lowltv", title: "Low LTV (Property-backed)", short: "Low LTV", suitable: "LAP, home loan, LRD — owned residential or commercial property", calc: lowLTVMethod, defaults: { propertyValue: 20000000, ltvPct: 60, monthlyIncome: 250000, foirPct: 60, existingEMI: 0, tenureMonths: 180, interestRatePct: 10.5 } },
  { key: "rtr-bt", title: "RTR — Balance Transfer", short: "RTR (BT)", suitable: "Existing loan with ≥12 months clean repayment track record", calc: rtrBTMethod, defaults: { outstandingPrincipal: 5000000, cleanRTRMonths: 18, topUpPct: 25, currentRatePct: 14, newRatePct: 11, remainingTenureMonths: 60 } },
  { key: "gross-profit", title: "Gross Profit Method", short: "Gross Profit", suitable: "Audited financials available · 3+ years of P&L", calc: grossProfitMethod, defaults: { annualRevenue: 60000000, annualCOGS: 42000000, foirPct: 65, existingEMI: 0, tenureMonths: 48, interestRatePct: 14 } },
  { key: "gross-receipts", title: "Gross Receipts (44ADA)", short: "Gross Receipts", suitable: "Doctors, CAs, lawyers, consultants — 44ADA presumptive income", calc: grossReceiptsMethod, defaults: { annualGrossReceipts: 12000000, presumptivePct: 50, foirPct: 65, existingEMI: 0, tenureMonths: 60, interestRatePct: 13 } },
  { key: "income-multiplier", title: "Income Multiplier (Salaried)", short: "Income×", suitable: "Salaried with 6+ months stable income · home/personal loans", calc: incomeMultiplierMethod, defaults: { netMonthlyTakeHome: 150000, foirPct: 55, existingEMI: 0, tenureMonths: 240, interestRatePct: 8.75, multiplierMonths: 72 } },
  { key: "ebitda", title: "Projected EBITDA", short: "EBITDA", suitable: "Project finance · CapEx · IT parks, factories, warehouses", calc: projectedEBITDAMethod, defaults: { projectedAnnualEBITDA: 50000000, targetDSCR: 1.25, tenureMonths: 84, interestRatePct: 11.5 } },
];
